import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Inbox, RefreshCw, Check, X, TrendingUp, Clock, Sparkles, User, Users } from "lucide-react";

// Compact analytics strip at the top of the renew-requests panel showing
// the "guest CTA → paid renewal" funnel over the last 30 days.
const RenewAnalyticsStrip = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/renew-requests/analytics?days=30");
      setData(r.data); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Analytics unavailable");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">{err}</div>;
  if (!data) return null;
  const noAsks = data.total_asks === 0;

  return (
    <div data-testid="renew-analytics-strip" className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
      <div className="rounded-xl border border-cream-200 bg-cream-100 p-3">
        <div className="text-[10px] uppercase tracking-wider text-navy-700/50">Asks (30d)</div>
        <div className="text-2xl font-bold text-navy-700 mt-1">{data.total_asks}</div>
      </div>
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <div className="text-[10px] uppercase tracking-wider text-emerald-700/70 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Conversion
        </div>
        <div className="text-2xl font-bold text-emerald-800 mt-1">{noAsks ? "—" : `${data.conversion_rate_pct}%`}</div>
        <div className="text-[10px] text-emerald-800/70 mt-0.5">
          {data.converted} of {data.total_asks} — {data.self_paid} paid, {data.comp} comp
        </div>
      </div>
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
        <div className="text-[10px] uppercase tracking-wider text-blue-700/70 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Avg time to renew
        </div>
        <div className="text-2xl font-bold text-blue-800 mt-1">
          {data.avg_time_to_convert_hours == null ? "—" : `${data.avg_time_to_convert_hours}h`}
        </div>
      </div>
      <div className="rounded-xl border border-cream-200 bg-white p-3">
        <div className="text-[10px] uppercase tracking-wider text-navy-700/50">Still open</div>
        <div className="text-2xl font-bold text-coral-500 mt-1">{data.still_open}</div>
        <div className="text-[10px] text-navy-700/50 mt-0.5">act on these first</div>
      </div>
      {data.top_events?.length > 0 && (
        <div className="col-span-full mt-1">
          <div className="text-[10px] uppercase tracking-wider text-navy-700/50 mb-1.5">Top events by demand</div>
          <ul className="flex flex-wrap gap-2">
            {data.top_events.slice(0, 6).map((e) => (
              <li key={e.event_id} className="text-[11px] px-2.5 py-1 rounded-full bg-cream-100 border border-cream-200">
                <span className="font-semibold text-navy-700">{e.event_name || "—"}</span>
                <span className="text-navy-700/60"> · {e.converted}/{e.asks}</span>
                {e.conversion_type === "self_paid" && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">paid</span>
                )}
                {e.conversion_type === "comp" && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-wider bg-purple-50 text-purple-700 rounded px-1.5 py-0.5">comp</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Guest-facing renew requests queue — populated when a guest lands on an
// expired gallery and clicks "Ask host to renew". Lets the admin see who's
// asking + mark each as resolved (auto-resolves on comp of the event).
const RenewRequestsPanel = () => {
  const [status, setStatus] = useState("open");
  const [typeFilter, setTypeFilter] = useState("all"); // all | host | guest
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [actionBusy, setActionBusy] = useState(null); // request_id currently acting on
  const [approveFor, setApproveFor] = useState(null); // request obj being approved
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/admin/renew-requests?status=${status}&limit=100`);
      setEntries(r.data.entries || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || `Failed to load (HTTP ${e?.response?.status || "network"}).`);
    } finally { setBusy(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/renew-requests/${id}/resolve`);
      await load();
    } catch { /* silent, keep list stable */ }
  };

  const approve = async () => {
    if (!approveFor) return;
    setActionBusy(approveFor.id);
    try {
      await api.post(`/admin/renew-requests/${approveFor.id}/approve`, {
        months: Number(months) || 1,
        reason: note.trim() || null,
      });
      setApproveFor(null); setNote(""); setMonths(1);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not approve this request.");
    } finally { setActionBusy(null); }
  };

  const deny = async (id) => {
    const reason = window.prompt("Optional note (sent to the host as the denial reason):", "") || "";
    if (reason === null) return; // user cancelled
    setActionBusy(id);
    try {
      await api.post(`/admin/renew-requests/${id}/deny`, { reason: reason.trim() || null });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not deny this request.");
    } finally { setActionBusy(null); }
  };

  const filtered = entries.filter((r) => {
    if (typeFilter === "all") return true;
    const t = r.type || "guest";
    return t === typeFilter;
  });

  return (
    <div data-testid="renew-requests-panel" id="renew-requests" className="bg-white rounded-3xl soft-shadow p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Inbox className="w-5 h-5 text-coral-300" />
          <h3 className="font-serif text-navy-700 text-xl">Renewal requests</h3>
          <span className="text-xs text-navy-700/50">{filtered.length} {status}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-cream-100 rounded-full p-1 text-xs">
            {["all", "host", "guest"].map((t) => (
              <button
                key={t} onClick={() => setTypeFilter(t)}
                data-testid={`renew-type-${t}`}
                className={`px-3 py-1 rounded-full font-semibold capitalize ${typeFilter === t ? "bg-navy-700 text-white" : "text-navy-700/70"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="inline-flex bg-cream-100 rounded-full p-1 text-xs">
            {["open", "approved", "converted", "resolved", "denied"].map((s) => (
              <button
                key={s} onClick={() => setStatus(s)}
                data-testid={`renew-tab-${s}`}
                className={`px-3 py-1 rounded-full font-semibold capitalize ${status === s ? "bg-coral-300 text-white" : "text-navy-700/70"}`}>
                {s}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={busy} data-testid="renew-refresh"
            className="inline-flex items-center gap-1.5 border border-navy-700/20 text-navy-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100 disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>
      <p className="text-sm text-navy-700/60 mt-2">
        Approve to extend the event's expiry for free (comp). Deny to leave the event as-is.
      </p>
      <RenewAnalyticsStrip />
      {err && <div className="mt-4 bg-red-50 text-red-700 text-sm rounded-md px-3 py-2">{err}</div>}
      {!err && filtered.length === 0 && !busy && (
        <p className="text-sm text-navy-700/50 py-6 text-center">No {status} {typeFilter !== "all" ? typeFilter + " " : ""}requests right now.</p>
      )}
      <ul className="mt-3 divide-y divide-cream-200">
        {filtered.map((r) => {
          const isHost = (r.type || "guest") === "host";
          return (
            <li key={r.id} data-testid={`renew-row-${r.id}`} className="py-3 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${
                    isHost ? "bg-blue-100 text-blue-700" : "bg-cream-200 text-navy-700/60"
                  }`}>
                    {isHost ? <><User className="w-2.5 h-2.5" /> Host</> : <><Users className="w-2.5 h-2.5" /> Guest</>}
                  </span>
                  <div className="font-medium text-navy-700 truncate">{r.event_name}</div>
                </div>
                <div className="text-[11px] text-navy-700/50 truncate mt-0.5">
                  {isHost ? (
                    <>
                      {r.host_name || r.host_email || "Host"}
                      {r.months_requested ? ` · asking ${r.months_requested}mo` : ""}
                      {r.desired_tier ? ` · tier ${r.desired_tier}` : ""}
                    </>
                  ) : (
                    <>
                      {r.guest_name || <span className="italic">Anonymous guest</span>}
                      {r.guest_email ? ` · ${r.guest_email}` : ""}
                    </>
                  )}
                  {" · " + new Date(r.created_at).toLocaleString(undefined, {dateStyle:"medium", timeStyle:"short"})}
                </div>
                {r.note && (
                  <div className="mt-1.5 text-sm text-navy-700/70 bg-cream-100 rounded-md px-3 py-2 italic">
                    “{r.note}”
                  </div>
                )}
                <div className="text-[10px] text-navy-700/40 font-mono truncate mt-1">event {r.event_id}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {r.status === "open" && (
                  <>
                    <button
                      onClick={() => { setApproveFor(r); setMonths(r.months_requested || 1); setNote(""); }}
                      disabled={actionBusy === r.id}
                      data-testid={`renew-approve-${r.id}`}
                      className="text-xs bg-emerald-500 text-white px-3 py-1.5 rounded-full font-semibold hover:bg-emerald-600 inline-flex items-center gap-1 whitespace-nowrap disabled:opacity-60">
                      <Sparkles className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button
                      onClick={() => deny(r.id)}
                      disabled={actionBusy === r.id}
                      data-testid={`renew-deny-${r.id}`}
                      className="text-xs bg-white text-red-600 border border-red-200 px-3 py-1.5 rounded-full font-semibold hover:bg-red-50 inline-flex items-center gap-1 whitespace-nowrap disabled:opacity-60">
                      <X className="w-3.5 h-3.5" /> Deny
                    </button>
                    <button
                      onClick={() => resolve(r.id)}
                      disabled={actionBusy === r.id}
                      data-testid={`renew-resolve-${r.id}`}
                      className="text-xs bg-cream-100 text-navy-700/70 px-3 py-1.5 rounded-full font-semibold hover:bg-cream-200 inline-flex items-center gap-1 whitespace-nowrap">
                      <Check className="w-3.5 h-3.5" /> Dismiss
                    </button>
                  </>
                )}
                {r.status === "approved" && (
                  <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded px-2 py-1 whitespace-nowrap">
                    Approved · {r.months_granted}mo
                  </span>
                )}
                {r.status === "denied" && (
                  <span className="text-[10px] uppercase tracking-wider bg-red-50 text-red-700 rounded px-2 py-1 whitespace-nowrap">Denied</span>
                )}
                {r.status === "resolved" && (
                  <span className="text-[10px] uppercase tracking-wider bg-cream-200 text-navy-700/70 rounded px-2 py-1 whitespace-nowrap">Dismissed</span>
                )}
                {r.status === "converted" && (
                  <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-1 whitespace-nowrap ${
                    r.conversion_type === "self_paid" ? "bg-emerald-100 text-emerald-800" : "bg-purple-100 text-purple-800"
                  }`}>
                    {r.conversion_type === "self_paid" ? "Converted (paid)" : r.conversion_type === "admin_approved" ? "Admin approved" : "Converted (comp)"}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Approve modal */}
      {approveFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/40" onClick={() => setApproveFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-serif italic text-navy-700 text-xl">Approve renewal</h4>
              <button onClick={() => setApproveFor(null)} className="text-navy-700/60 hover:text-coral-300 text-2xl leading-none">×</button>
            </div>
            <p className="text-sm text-navy-700/70">
              Extend <strong>{approveFor.event_name}</strong>'s expiry — host will get a confirmation email.
            </p>
            <label className="block text-xs font-semibold text-navy-700/70 mt-4 mb-1">Months to extend</label>
            <input
              type="number" min="1" max="24"
              value={months} onChange={(e) => setMonths(e.target.value)}
              data-testid="renew-approve-months"
              className="w-full px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
            />
            <label className="block text-xs font-semibold text-navy-700/70 mt-3 mb-1">Internal note (optional)</label>
            <textarea
              rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Logged in the sponsor-log audit trail"
              data-testid="renew-approve-note"
              className="w-full px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
            />
            <div className="mt-4 flex gap-2">
              <button
                onClick={approve}
                disabled={actionBusy === approveFor.id}
                data-testid="renew-approve-confirm"
                className="flex-1 bg-emerald-500 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60">
                {actionBusy === approveFor.id ? "Approving…" : "Approve & extend"}
              </button>
              <button onClick={() => setApproveFor(null)}
                className="px-4 py-2 rounded-full text-sm border border-navy-700/20 text-navy-700 hover:bg-cream-100">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenewRequestsPanel;
